import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverCostOfLivingHacks() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-cost-of-living-hacks";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Housing",
    "section-2": "Transportation",
    "section-3": "Groceries and Dining",
    "section-4": "Entertainment and Recreation",
    "section-5": "Utilities and Bills",
    locations: "Key Locations",
    "local-regulations": "Local Regulations / Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.translink.ca/",
      label: "TransLink Fares & Passes",
    },
    {
      url: "https://www.bchousing.org/housing-assistance/rental-assistance",
      label: "BC Housing Rental Assistance Programs",
    },
    {
      url: "https://www.bchydro.com/powersmart.html",
      label: "BC Hydro Energy Saving Tips",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "loc-vpl",
      lat: 49.27980,
      lng: -123.11570,
      popupText: "Vancouver Public Library – Central Branch",
    },
    {
      id: "loc-stanley",
      lat: 49.30430,
      lng: -123.14430,
      popupText: "Stanley Park",
    },
    {
      id: "loc-sunrise",
      lat: 49.28300,
      lng: -123.09900,
      popupText: "Sunrise Market (Chinatown)",
    },
    {
      id: "loc-gathering",
      lat: 49.27800,
      lng: -123.12350,
      popupText: "Gathering Place Community Centre",
    },
    {
      id: "loc-commbroad",
      lat: 49.26250,
      lng: -123.06890,
      popupText: "Commercial–Broadway SkyTrain Station",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // Mobile map reference
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Cost of Living Hacks: Practical Tips to Save</title>
        <meta
          name="description"
          content="An in-depth guide on reducing expenses in Vancouver, including affordable housing, transportation, groceries, utilities, and entertainment."
        />
        <meta
          name="keywords"
          content="Vancouver cost of living, Vancouver budget hacks, living cheaply in Vancouver"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-cost-of-living-hacks.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Cost of Living Hacks: Practical Tips to Save"
        />
        <meta
          property="og:description"
          content="Learn how to save money on housing, transportation, groceries, entertainment, and utilities in Vancouver."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-cost-of-living-hacks.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-cost-of-living-hacks.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Cost of Living Hacks"
        />
        <meta
          name="twitter:description"
          content="Comprehensive strategies to save on living costs in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-cost-of-living-hacks.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/vancouver-cost-of-living-hacks.jpg"
              }
              alt="Vancouver cityscape on a budget"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* INTRODUCTION */}
            <h2 id="intro">Introduction</h2>
            <p>
              Living in Vancouver can be expensive, but it doesn’t have to be
              overwhelming. With the right knowledge, you can minimize costs
              and still make the most of this vibrant, multicultural city. This
              comprehensive guide offers **practical cost-saving hacks** for
              housing, transportation, groceries, utilities, and entertainment.
              Whether you’re a student, a new immigrant, or a local resident
              looking to tighten your budget, these tips will help you enjoy
              Vancouver without breaking the bank.
            </p>

            {/* SECTION 1: HOUSING */}
            <h2 id="section-1">Housing</h2>
            <p>
              **1. Affordable Neighborhoods:** Vancouver's real estate is among
              the priciest in Canada, but you can still find relatively cheaper
              pockets, especially in East Vancouver or South Vancouver. Areas
              like <em>Sunset, Victoria–Fraserview, Killarney, Renfrew–Collingwood</em>,
              and <em>Marpole</em> often boast lower rents compared to downtown.
              You might also explore nearby cities like Burnaby or New Westminster,
              where rent can be lower, though you’ll need to balance cost savings
              against longer commutes.
            </p>
            <p>
              **2. Renting vs. Buying:** The average detached home price in
              Vancouver is well over $1 million, making mortgages very high.
              Renting is generally cheaper monthly and requires less money up
              front. If you do plan to stay long-term and can afford a large
              down payment, you could build equity over time by buying. However,
              from a strictly monthly-expense standpoint, renting nearly always
              wins for cost savings in this market.
            </p>
            <p>
              **3. Finding Budget-Friendly Rentals:** Start searching early on
              Craigslist, Kijiji, or local Facebook groups. Look for older
              low-rise buildings or basement suites instead of brand-new condos
              to keep rental costs down. Sharing a home with roommates cuts not
              just rent but also utilities and internet. Students can leverage
              campus housing boards or co-op housing (though expect waitlists).
            </p>
            <p>
              **4. Shared Housing Options:** Co-living is popular in Vancouver
              because it’s a great way to reduce expenses. Consider renting a
              larger place with friends to split the costs. You’ll find many
              sublets and shared housing opportunities on social media or
              specialized roommate sites. Also, basement suites or laneway houses
              are common in Vancouver and can be cheaper alternatives to full
              apartments.
            </p>

            {/* SECTION 2: TRANSPORTATION */}
            <h2 id="section-2">Transportation</h2>
            <p>
              **1. Public Transit Savings:** Vancouver’s transit system, covering
              buses, SkyTrain, and SeaBus, is run by TransLink. Buying a Compass
              Card (stored value) is cheaper than cash fares, and you can transfer
              freely for 90 minutes. If you commute daily, a monthly pass or
              discounted student pass (U-Pass) can save you a lot. Seniors and
              youth get concession discounts, and some workplaces subsidize
              transit passes for employees. Always tap in and out to avoid any
              overcharge or fines.
            </p>
            <p>
              **2. Biking & Bike-Sharing:** Vancouver ranks highly for its
              cyclist-friendly infrastructure. The extensive bike lanes make
              cycling not only healthy but cost-effective. If you don’t own a
              bike, check out <em>Mobi by Shaw Go</em>, the city’s bike-sharing
              service. They offer passes that can be significantly cheaper than
              using a car for short commutes. Low-income residents may qualify
              for a Community Pass at a reduced annual rate.
            </p>
            <p>
              **3. Car-Sharing & Ride-Hailing:** Owning a car in Vancouver means
              high insurance, pricey gas, and parking fees. Instead, consider
              car-sharing services like <em>Evo</em> and <em>Modo</em> which let
              you book by the minute or hour, eliminating big recurring costs.
              If you need to use Uber or Lyft, keep an eye out for occasional
              promos and share rides with friends. Carpooling or ride boards at
              universities is another way to minimize transportation spending.
            </p>
            <p>
              **4. Parking on a Budget:** Downtown parking can be expensive.
              Using Park & Ride at SkyTrain stations, leveraging free evening or
              weekend street parking, or simply ditching the car entirely can
              save hundreds of dollars a month. If you do need to drive, car
              shares like Evo often include free street parking in many zones.
            </p>

            {/* SECTION 3: GROCERIES & DINING */}
            <h2 id="section-3">Groceries and Dining</h2>
            <p>
              **1. Budget Grocery Stores & Discount Days:** Seek out
              cost-effective chains like <em>No Frills</em>, <em>Real Canadian
              Superstore</em>, and <em>Walmart</em> for staples. Many shops have
              discount days (like the first Tuesday of the month) offering 10%
              off. Smaller spots like <em>Sunrise Market</em> in Chinatown can
              be a goldmine for cheap produce. Look for clearance racks and
              consider buying in bulk when items are on sale.
            </p>
            <p>
              **2. Affordable Restaurants & Food Markets:** Vancouver has
              myriad low-cost ethnic eateries. Look for lunch specials at sushi
              places, hole-in-the-wall pho joints, or Indian buffets around
              Main Street and Kingsway. Food courts and night markets can also
              be cheaper than standard restaurants. Happy hours (around 3–6 pm)
              at many bars and restaurants offer discounted drinks and appetizers.
              If you’re on a very tight budget, check out community meal programs
              or the Greater Vancouver Food Bank for extra help.
            </p>
            <p>
              **3. Meal Prepping & Community Kitchens:** Meal prepping at home
              is one of the best ways to cut your food bill. Cook big batches
              of soups, stir-fries, or pasta and portion them out for the week.
              This habit also helps avoid expensive takeout. Some community
              centres host kitchen programs where groups cook together and
              share costs. If your schedule is busy, devote a weekend afternoon
              to preparing multiple meals—your fridge (and wallet) will thank you.
            </p>
            <p>
              **4. Community Food Programs:** Low-income individuals can access
              free or low-cost groceries through programs like the Greater
              Vancouver Food Bank or local community fridges. The <em>Gathering
              Place Community Centre</em> in Downtown offers extremely affordable
              meals (some under $3), along with drop-in programs and social
              support services.
            </p>

            {/* SECTION 4: ENTERTAINMENT & RECREATION */}
            <h2 id="section-4">Entertainment and Recreation</h2>
            <p>
              Vancouver is known for its stunning natural beauty and accessible
              outdoor spaces—often free or very low cost. **Parks and beaches**
              are the best examples: from <em>Stanley Park</em> to <em>Kitsilano
              Beach</em>, you can spend hours enjoying trails, picnics, or the
              sunset for no charge. <em>Lynn Canyon Park</em> in North Vancouver
              features a free suspension bridge and hiking trails.
            </p>
            <p>
              **1. Free Attractions:** Walking or biking the Seawall offers
              wonderful city and water views. Parks like <em>Queen Elizabeth
              Park</em> provide scenic vistas of downtown, and the park entrance
              is free (though the Bloedel Conservatory inside has a small fee).
              Many museums or galleries have free community days or by-donation
              evenings. The Vancouver Public Library system not only lets you
              borrow books and movies but also hosts free talks, workshops, and
              events.
            </p>
            <p>
              **2. Local Festivals and Events:** Look out for street festivals
              like Car Free Day, Italian Day, or the Chinatown Night Market.
              Summertime sees free outdoor movies in parks and the Celebration
              of Light fireworks over English Bay. Markets like <em>Granville
              Island Public Market</em> are fun to explore without spending much,
              though you might be tempted by the food stalls.
            </p>
            <p>
              **3. Budget Nightlife & Culture:** Nightlife doesn’t have to be
              pricey. Many bars and pubs have no cover charge. Look for open-mic
              nights or local bands performing. Sometimes heading to a club
              early (before 10 pm) waives the cover fee. To catch movies,
              consider discount Tuesdays at certain theaters. For broader
              cultural experiences, check free or donation-based gallery
              openings or community events posted online.
            </p>
            <p>
              **4. Community Centres & Library Memberships:** Vancouver’s
              community centres offer fitness classes, gyms, or skating rinks
              at low drop-in fees (often under $5). If you qualify as low-income,
              the Leisure Access Program may give you free or heavily discounted
              admission to pools and rinks. Libraries also lend out instruments
              like guitars at select branches—this city truly embraces making
              resources accessible.
            </p>

            {/* SECTION 5: UTILITIES & BILLS */}
            <h2 id="section-5">Utilities and Bills</h2>
            <p>
              **1. Reducing Hydro Costs:** BC Hydro rates can be managed by
              adopting simple energy-saving habits. Turn off lights and
              appliances when not in use, switch to LED bulbs, and reduce
              your thermostat a couple of degrees. BC Hydro provides online
              tracking of your hourly usage and sometimes offers free
              <em>Energy Saving Kits</em> to low-income customers. Wash laundry
              in cold water and line-dry when possible to cut down on bills.
            </p>
            <p>
              **2. Heating & Water Conservation:** If you pay for your own
              heat, weatherproof your windows and doors to keep warmth inside.
              Consider lowering the hot water tank temperature slightly. The
              city occasionally imposes summer water restrictions, so be mindful
              of lawn watering schedules or you could face fines.
            </p>
            <p>
              **3. Internet & Mobile Plans:** Canada’s telecom costs are
              notoriously high. Look at smaller or discount providers rather than
              the big players, or consider prepaid plans. Telus offers programs
              like “Internet for Good” or “Mobility for Good” for eligible
              low-income households or seniors, drastically reducing monthly bills.
            </p>
            <p>
              **4. Government Assistance Programs:** Don’t overlook provincial or
              federal programs. BC Housing’s Rental Assistance Program (RAP) or
              Shelter Aid for Elderly Renters (SAFER) can provide monthly subsidies.
              BC Hydro’s <em>Customer Crisis Fund</em> may help if you’re suddenly
              unable to pay your electricity bill. File your taxes to ensure you
              get GST credits or Climate Action Tax Credits. All these small
              measures add up.
            </p>

            {/* KEY LOCATIONS */}
            <h2 id="locations">Key Locations</h2>
            <ul>
              <li
                ref={(el) => setSpotRef("loc-vpl", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver Public Library – Central Branch</strong>{" "}
                <Marker
                  coordY={49.27980}
                  coordX={-123.11570}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : A major resource for free entertainment and learning. Membership
                gives access to books, movies, workshops, and Wi-Fi at no cost.
              </li>
              <li
                ref={(el) => setSpotRef("loc-stanley", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Stanley Park</strong>{" "}
                <Marker
                  coordY={49.30430}
                  coordX={-123.14430}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : An expansive public park offering free beaches, trails, and
                incredible views. Perfect for picnics, jogging, or cycling.
              </li>
              <li
                ref={(el) => setSpotRef("loc-sunrise", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Sunrise Market (Chinatown)</strong>{" "}
                <Marker
                  coordY={49.28300}
                  coordX={-123.09900}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : A family-run store known for extremely cheap produce and
                groceries—an essential stop for budget-conscious shoppers.
              </li>
              <li
                ref={(el) => setSpotRef("loc-gathering", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Gathering Place Community Centre</strong>{" "}
                <Marker
                  coordY={49.27800}
                  coordX={-123.12350}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Provides hot meals under $3, along with social services
                and drop-in programs, making it a vital hub for those on a
                tight budget.
              </li>
              <li
                ref={(el) => setSpotRef("loc-commbroad", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Commercial–Broadway SkyTrain Station</strong>{" "}
                <Marker
                  coordY={49.26250}
                  coordX={-123.06890}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : A key transit junction linking multiple SkyTrain lines
                and bus routes for convenient, low-cost travel around Greater Vancouver.
              </li>
            </ul>

            {/* LOCAL REGULATIONS & BEST PRACTICES */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              - **Tenant Rights & Rent Increases:** BC law limits annual rent
              increases (e.g., 3.5% in 2024). Landlords must give written notice,
              and large sudden hikes are typically disallowed without special
              approval. Always sign a written agreement, keep documentation, and
              perform a move-in inspection to protect your deposit.  
              - **Transit Rules:** Always pay your fare; getting caught without
              proof of payment can lead to a $173 fine. Priority seating is
              expected for seniors, pregnant women, or people with disabilities.  
              - **Cycling Laws:** Helmets are mandatory in BC, and bikes must
              have a front white light and rear red light after dark. You can
              be fined if you break these rules.  
              - **Parking and Car Share:** Pay attention to metered hours and
              residential zones. Car-sharing services have rules for where you
              can end your trip.  
              - **Alcohol Consumption:** Drinking in public spaces is generally
              prohibited except in specific pilot project areas.  
              - **Waste Management:** Separate garbage, recycling, and compost
              properly. Fines can be issued for repeated violations.  
              - **Water Restrictions:** Seasonal watering restrictions can incur
              fines if not followed.
            </p>
            <p>
              Always verify the latest regulations with the <em>City of Vancouver</em>
              or official government websites; rules and guidelines may change
              over time.
            </p>

            {/* FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What are the cheapest neighborhoods in Vancouver?</h3>
              <p>
                Look to East Vancouver (e.g., Sunset, Victoria–Fraserview, Killarney,
                Renfrew–Collingwood) or South Vancouver (Marpole). These areas tend
                to have cheaper rents than downtown. Basement suites or shared
                housing arrangements can further reduce monthly costs.
              </p>

              <h3>2. How can I cut my grocery bill in half?</h3>
              <p>
                Focus on discount grocers (No Frills, Superstore, Walmart), shop on
                discount days, and buy in bulk when possible. Visit local produce
                markets like Sunrise Market for very cheap fruits and veggies. Meal
                prepping at home also saves substantially compared to eating out.
              </p>

              <h3>3. Are there free recreational options in Vancouver?</h3>
              <p>
                Absolutely. Vancouver’s beaches, parks, and public spaces are world
                class and free to visit. Stanley Park, Lynn Canyon, Queen Elizabeth
                Park, and the seawall are top picks for no-cost fun. Watch local
                event listings for free outdoor concerts, festivals, and cultural
                celebrations.
              </p>

              <h3>4. What if I can’t afford my rent or bills anymore?</h3>
              <p>
                Check for government or non-profit assistance. BC Housing offers
                Rental Assistance Programs, BC Hydro has a Customer Crisis Fund,
                and there are food banks or community meal services (like Gathering
                Place) if you need extra support. Always communicate with your
                landlord or utility provider early if you see trouble coming.
              </p>
            </div>

            {/* CONCLUSION */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              By applying these tips—choosing budget-friendly neighborhoods, using
              public transit or bikes, shopping smart for groceries, and
              capitalizing on free entertainment—you can significantly reduce
              your monthly expenses in Vancouver. The city may be known for
              high living costs, but with planning and creativity, you can enjoy
              its many opportunities without draining your finances. Seek out
              local resources, stay up to date on regulations, and stay proactive
              about managing your budget.
            </p>

            <p>
              Remember: small changes add up. Whether you’re a student trying
              to make ends meet or a newcomer settling into the city, these
              **cost-of-living hacks** will help you thrive in Vancouver.
            </p>

            {/* OPTIONAL: DISCLAIMER (SHORT) */}
            <h4>Disclaimer</h4>
            <p>
              This guide provides general information based on publicly available
              data and personal experiences. Always verify current details (rent
              increase limits, transit fares, program eligibility) from official
              sources, as policies and prices can change over time. Nothing here
              constitutes legal or financial advice.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}